import React, { useContext, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import CloseIcon from '@mui/icons-material/Close'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import API from '../../../../services/API'
import { GlobalContext, UserContext } from '../../../../App'
import { uploadDocument } from '../../../../helpers/uploadDocument'
import {
  IMAGE_EXTENSIONS,
  extensionOf,
} from '../../../../components/Documents/useDocumentPreview'

const ACCEPTED_TYPES = [
  'image/*',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.gpx',
  'application/gpx+xml',
].join(',')

// Drag and drop ignores the input's `accept`, so dropped files are checked
// against the same rules (extension, MIME wildcard or MIME type — same
// matching as helpers/DocumentsField.jsx).
const isAccepted = (file) => {
  const extension = extensionOf(file.name)
  return ACCEPTED_TYPES.split(',').some((rule) => {
    if (rule.startsWith('.')) return extension === rule.slice(1)
    if (rule.endsWith('/*')) {
      return (
        file.type.startsWith(rule.slice(0, -1)) ||
        (rule === 'image/*' && IMAGE_EXTENSIONS.includes(extension))
      )
    }
    return file.type === rule || extension === rule.split('/')[1]
  })
}

const fileID = (file) => `${file.name}-${file.size}-${file.lastModified}`

const AddNewDocument = ({ eventID, onClose, setDocuments }) => {
  const fileInput = useRef()
  const [items, setItems] = useState([]) // [{ id, file, name }]
  const [dragging, setDragging] = useState(false)
  const [skippedMessage, setSkippedMessage] = useState(null)
  const [visibleToAllStaff, setVisibleToAllStaff] = useState(false)
  const [saving, setSaving] = useState(false)
  const [savingIndex, setSavingIndex] = useState(0)
  const { setToast } = useContext(GlobalContext)
  const { user } = useContext(UserContext)

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList ?? [])
    if (!incoming.length) return
    const fresh = incoming.filter(
      (file) =>
        isAccepted(file) && !items.some((item) => item.id === fileID(file)),
    )
    const skipped = incoming.length - fresh.length
    setSkippedMessage(
      skipped > 0
        ? `${skipped} ${skipped === 1 ? 'file was' : 'files were'} skipped — only images, PDFs, Word documents and GPX files can be added, once each.`
        : null,
    )
    setItems([
      ...items,
      ...fresh.map((file) => ({
        id: fileID(file),
        file,
        // Default the display name to the file name, minus its extension
        name: file.name.replace(/\.[^.]+$/, ''),
      })),
    ])
  }

  const updateName = (id, name) =>
    setItems((current) =>
      current.map((item) => (item.id === id ? { ...item, name } : item)),
    )

  const removeItem = (id) =>
    setItems((current) => current.filter((item) => item.id !== id))

  const handleFileChange = (e) => {
    addFiles(e.target.files)
    // Reset so choosing the same file again still fires onChange
    e.target.value = ''
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (saving || !items.length) return
    if (items.some((item) => !item.name.trim())) return

    setSaving(true)
    const savedIds = []
    let failure = null
    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      setSavingIndex(i)
      try {
        const s3key = await uploadDocument(item.file, 'events/documents')
        const res = await API.post('/event/add-document', {
          eventID,
          documentName: item.name.trim(),
          visibleToAllStaff,
          s3key,
        })
        if (res.status !== 200) {
          throw new Error('Unable to save the document.')
        }
        setDocuments((prev) => [
          ...(prev ?? []),
          {
            ...res.data,
            author: { firstName: user.firstName, lastName: user.lastName },
          },
        ])
        savedIds.push(item.id)
      } catch (error) {
        failure = { item, message: error?.message }
        break // failures are usually systemic - don't spam one per file
      }
    }
    setSaving(false)

    if (!failure) {
      setToast({
        message:
          savedIds.length === 1
            ? 'Document uploaded'
            : `${savedIds.length} documents uploaded`,
      })
      onClose()
    } else {
      // Saved files leave the list, so retrying can't create duplicates
      setItems((current) =>
        current.filter((item) => !savedIds.includes(item.id)),
      )
      setToast({
        message: `${failure.item.file.name}: ${
          failure.message || 'Unable to upload the document.'
        }`,
        severity: 'error',
      })
    }
  }

  const namesValid = items.every((item) => item.name.trim())

  return (
    <Stack
      gap={1.5}
      component="form"
      onSubmit={handleSubmit}
      padding={1}
      sx={{ width: 480, maxWidth: '80vw' }}
    >
      <Stack gap={0.25}>
        <Typography variant="h6" fontWeight={800}>
          Upload documents
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Images, PDFs, Word documents and GPX files are accepted.
        </Typography>
      </Stack>
      <Box
        onClick={() => !saving && fileInput.current.click()}
        onDragOver={(e) => {
          e.preventDefault()
          if (!saving) setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragging(false)
          if (!saving) addFiles(e.dataTransfer.files)
        }}
        sx={{
          p: 2,
          borderRadius: 2,
          border: '1px dashed',
          borderColor: dragging ? 'success.main' : 'rgba(15, 23, 42, 0.25)',
          bgcolor: dragging ? 'rgba(0, 150, 76, 0.06)' : '#f8fafc',
          cursor: 'pointer',
          textAlign: 'center',
          // Children would otherwise fire dragleave as the pointer crosses them
          '& > *': { pointerEvents: 'none' },
        }}
      >
        <Stack alignItems="center" gap={0.5}>
          <UploadFileIcon color={dragging ? 'success' : 'action'} />
          <Typography variant="body2" fontWeight={700}>
            Choose files or drag them here
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Click to browse
          </Typography>
        </Stack>
      </Box>
      <input
        ref={fileInput}
        type="file"
        multiple
        style={{ display: 'none' }}
        accept={ACCEPTED_TYPES}
        onChange={handleFileChange}
      />
      {skippedMessage && (
        <Typography variant="caption" color="error">
          {skippedMessage}
        </Typography>
      )}
      {items.map((item, i) => (
        <Stack key={item.id} direction="row" alignItems="center" gap={1}>
          <TextField
            value={item.name}
            required
            onChange={(e) => updateName(item.id, e.target.value)}
            label="Document name"
            autoComplete="off"
            size="small"
            fullWidth
            disabled={saving}
            error={!item.name.trim()}
            helperText={
              saving
                ? i < savingIndex
                  ? 'Uploaded'
                  : i === savingIndex
                    ? 'Uploading…'
                    : 'Waiting…'
                : item.file.name
            }
          />
          <IconButton
            size="small"
            onClick={() => removeItem(item.id)}
            disabled={saving}
            aria-label={`Remove ${item.file.name}`}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      ))}
      <FormControlLabel
        label={
          <Stack>
            <Typography variant="body2">Viewable by all staff</Typography>
            <Typography variant="caption" color="text.secondary">
              Shows on the read-only event view; otherwise planners only.
            </Typography>
          </Stack>
        }
        control={
          <Switch
            checked={visibleToAllStaff}
            onChange={(e) => setVisibleToAllStaff(e.target.checked)}
            color="success"
            disabled={saving}
          />
        }
      />
      <Stack direction="row" gap={1} justifyContent="flex-end">
        <Button variant="outlined" color="inherit" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          type="submit"
          disabled={!items.length || !namesValid || saving}
          startIcon={
            saving ? <CircularProgress size="1rem" color="inherit" /> : null
          }
        >
          {saving
            ? items.length === 1
              ? 'Uploading'
              : `Uploading ${savingIndex + 1} of ${items.length}`
            : items.length > 1
              ? `Upload ${items.length} documents`
              : 'Upload'}
        </Button>
      </Stack>
    </Stack>
  )
}

export default AddNewDocument
